/**
 * Public /prices — full pricing catalogue for Functional LAB.
 *
 * Layout (per user spec):
 *   1. À la carte session prices (table) — SHOWN FIRST
 *   2. Rehabilitation packages (Weekly / Pay-in-full selectable tiles)
 *   3. Monthly memberships
 *   4. Payment conditions footer
 *
 * The "Choose this package" CTA on each package is auth-aware:
 *   - Logged in → straight to /app/packages?buy={id}&plan={plan} (auto-checkout)
 *   - Logged out → /register?package={id}&plan={plan}
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import PackageDetailsModal from "@/components/PackageDetailsModal";
import { usePageCms } from "@/lib/siteSettings";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ArrowRight, Check, Info, ShieldCheck } from "lucide-react";
import { CategoryIcon } from "@/lib/categoryIcons";
import { REHAB_PACKAGES as PACKAGES } from "@/config/servicePackages";
import {
  APPROVED_MEM_PRICE, APPROVED_MEM_FEATURES, APPROVED_MEM_TAGLINE,
  APPROVED_MEMBERSHIP_IDS, applyApprovedServicePricing, ASSESSMENT_CREDIT_PROMO,
} from "@/config/approvedPricing2026";
import { useHeaderHeight, useElementHeight } from "@/hooks/useStickyOffset";

export default function Prices() {
  const { user } = useAuth();
  const location = useLocation();
  const [memberships, setMemberships] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [onlineProgrammes, setOnlineProgrammes] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  // Top-level tab: which of the four price categories is showing.
  const [activeMain, setActiveMain] = useState("alacarte");
  // Deep-link support: /prices#memberships or #packages open the matching tab.
  useEffect(() => {
    if (!location.hash) return;
    const h = location.hash.replace("#", "");
    if (h === "memberships") setActiveMain("memberships");
    else if (h === "packages" || h === "programmes") setActiveMain("packages");
    else if (h === "online-programmes") setActiveMain("online");
    else if (h.startsWith("cat-")) setActiveMain("alacarte");
  }, [location.hash]);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const cms = usePageCms("prices");
  // Sticky offset — compute at runtime so the tab bar sits *exactly* under
  // the fixed nav on every screen size + resizes with the nav's scroll shrink.
  const headerH = useHeaderHeight();
  const mainTabsRef = useRef(null);
  const mainTabsH = useElementHeight(mainTabsRef);
  // Which à-la-carte category tab is open. Initialised to the first non-empty
  // category once the catalogue lands.
  const [activeCat, setActiveCat] = useState(null);
  useEffect(() => {
    if (!activeCat && catalogue.length > 0) setActiveCat(catalogue[0].slug);
  }, [catalogue, activeCat]);

  useEffect(() => {
    api.get("/packages").then((r) => {
      const list = (r.data || []).filter((p) => p.active !== false);
      list.sort((a, b) => (a.display_order || 100) - (b.display_order || 100));
      // RUN A (preview): show only the three approved memberships, with the
      // approved DISPLAY prices and feature lists. Checkout prices are
      // unchanged in the backend, so the Join CTA is disabled below.
      const approvedMems = list
        .filter((p) => p.type === "membership" && APPROVED_MEMBERSHIP_IDS.includes(p.id))
        .map((m) => ({
          ...m,
          price: APPROVED_MEM_PRICE[m.id],
          card_highlights: APPROVED_MEM_FEATURES[m.id] || m.card_highlights,
          short_description: APPROVED_MEM_TAGLINE[m.id] || m.short_description,
          billing: undefined,
          _approved: true,
        }));
      setMemberships(approvedMems);
      setProgrammes(list.filter((p) => p.type === "package"));
      setOnlineProgrammes(list.filter((p) => p.type === "online_programme"));
    }).catch(() => {});
    api.get("/services/public").then((r) => {
      const cats = (r.data?.categories || []).filter((c) => (c.services || []).length > 0);
      setCatalogue(applyApprovedServicePricing(cats));
    }).catch(() => setCatalogue([]));
  }, []);

  // Map membership.id → membership.name for the "Included in ..." badges.
  const memNameById = useMemo(() => {
    const map = {};
    for (const m of memberships) map[m.id] = m.name.replace(" Membership", "");
    return map;
  }, [memberships]);

  // Reverse map membership.id → list of {name, category} services included in it.
  const memPerks = useMemo(() => {
    const map = {};
    for (const c of catalogue) {
      for (const s of c.services) {
        for (const mid of (s.included_in || [])) {
          (map[mid] = map[mid] || []).push({ name: s.name, cat: c.slug });
        }
      }
    }
    return map;
  }, [catalogue]);

  // Router path used when a visitor clicks "Book" on an à-la-carte row.
  // We route through /checkout/summary so buyers pay upfront (Stripe) and
  // the appointment picker on the other side already knows they've bought a
  // session credit. Logged-out users are handled inside CheckoutSummary
  // (guest checkout OR "Create account & pay"), so this href is the same
  // whether or not the visitor is signed in.
  const bookHref = (svc) => `/checkout/summary?package=srv_${svc.id}&plan=full`;

  // Given a service, find the cheapest membership that already includes it —
  // that's the best upsell to nudge the visitor towards.
  const upsell = (svc) => {
    if (!(svc.included_in || []).length || !memberships.length) return null;
    const candidates = memberships.filter((m) => (svc.included_in || []).includes(m.id));
    if (!candidates.length) return null;
    candidates.sort((a, b) => (a.price || 0) - (b.price || 0));
    return candidates[0];
  };

  // If the visitor arrived at /prices#cat-massage, open that tab (and
  // scroll into view) once the catalogue is loaded.
  useEffect(() => {
    if (!location.hash || catalogue.length === 0) return;
    const id = location.hash.replace("#", "");
    if (id.startsWith("cat-")) {
      const slug = id.slice(4);
      if (catalogue.some((c) => c.slug === slug)) setActiveCat(slug);
    }
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [location.hash, catalogue]);

  return (
    <div className="fl-page min-h-screen">
      <PublicHeader />

      {/* ================ Hero ================ */}
      {cms.isVisible("hero") && (
      <section className="pt-32 md:pt-40 pb-20 md:pb-24 px-6 bg-[#0A0A0A] fl-noise relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="fl-label text-[#f47a20]">{cms.text("hero", "eyebrow", "Pricing")}</div>
          <h1 className="fl-heading text-5xl md:text-6xl mt-3">{cms.text("hero", "heading", "Transparent pricing — pay your way.")}</h1>
          <p className="text-zinc-400 mt-4 max-w-2xl">
            One-off sessions, structured rehab packages, or a monthly membership — every option is designed around your recovery goals.
          </p>
        </div>
      </section>
      )}

      {/* ================ Main category selector ================ */}
      <div
        ref={mainTabsRef}
        role="tablist"
        aria-label="Pricing categories"
        className="sticky z-30 border-b backdrop-blur"
        style={{
          top: headerH,
          borderColor: "var(--fl-border)",
          background: "color-mix(in oklab, var(--fl-bg) 92%, transparent)",
        }}
        data-testid="prices-main-tabs"
      >
        <div className="max-w-7xl mx-auto px-6 flex gap-1 md:gap-3 overflow-x-auto py-3 no-scrollbar">
          {[
            { key: "alacarte",    label: "À la carte prices", sub: "Individual sessions" },
            { key: "memberships", label: "Memberships",       sub: "Monthly plans" },
            { key: "packages",    label: "Packages & Other",  sub: "Rehab programmes" },
            { key: "online",      label: "Online programmes", sub: "6-week self-guided" },
          ].map((t) => {
            const active = activeMain === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setActiveMain(t.key)}
                className={`fl-btn-anim shrink-0 text-left px-4 md:px-5 py-2.5 rounded-xl transition-colors ${
                  active ? "bg-[#f47a20] text-white" : "bg-transparent border hover:border-[#f47a20]/60"
                }`}
                style={!active ? { borderColor: "var(--fl-border)", color: "var(--fl-text)" } : {}}
                data-testid={`prices-main-tab-${t.key}`}
              >
                <div className="fl-heading text-sm md:text-base whitespace-nowrap">{t.label}</div>
                <div className={`text-[10px] md:text-[11px] mt-0.5 whitespace-nowrap ${active ? "text-white/80" : "text-zinc-500"}`}>{t.sub}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ================ À la carte — tabbed selector ================ */}
      <section className={`py-24 md:py-32 bg-[#121212] border-t border-white/5 ${activeMain !== "alacarte" ? "hidden" : ""}`} data-testid="prices-section-alacarte">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-6">
            <div>
              <div className="fl-label text-[#f47a20]">À la carte</div>
              <h2 className="fl-heading text-3xl md:text-4xl mt-2">Individual session prices</h2>
            </div>
            <div className="text-xs text-zinc-500 max-w-md text-right">Pick a category to see the sessions available at pay-as-you-go rates.</div>
          </div>

          {catalogue.length === 0 ? (
            <div className="fl-card p-8 text-center text-zinc-500 text-sm">Loading pricing…</div>
          ) : (
            <div className="fl-card fl-card-elevated p-4 md:p-6" data-testid="alacarte-tabbed">
              {/* Category tabs — sticky so users can switch category from anywhere in the section */}
              <div
                role="tablist"
                aria-label="À la carte categories"
                className="sticky z-20 -mx-4 md:-mx-6 px-4 md:px-6 pt-3 pb-3 flex flex-nowrap gap-2 border-b backdrop-blur overflow-x-auto no-scrollbar"
                style={{
                  top: headerH + mainTabsH,
                  borderColor: "var(--fl-border)",
                  background: "color-mix(in oklab, var(--fl-bg-2) 92%, transparent)",
                }}
                data-testid="alacarte-tabs"
              >
                {catalogue.map((cat) => {
                  const isActive = activeCat === cat.slug;
                  return (
                    <button
                      key={cat.slug}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveCat(cat.slug)}
                      className={`fl-btn-anim inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full transition-colors ${
                        isActive
                          ? "bg-[#f47a20] text-white"
                          : "bg-transparent border hover:border-[#f47a20]/60 text-zinc-300"
                      }`}
                      style={!isActive ? { borderColor: "var(--fl-border)" } : {}}
                      data-testid={`alacarte-tab-${cat.slug}`}
                    >
                      <CategoryIcon name={cat.icon} size={13} className={isActive ? "text-white" : "text-[#f47a20]"} />
                      <span className="font-semibold tracking-wide">{cat.name}</span>
                      <span className={`text-[10px] ${isActive ? "text-white/80" : "text-zinc-500"}`}>{cat.services.length}</span>
                    </button>
                  );
                })}
              </div>

              {/* Active category panel */}
              {(() => {
                const cat = catalogue.find((c) => c.slug === activeCat) || catalogue[0];
                if (!cat) return null;
                return (
                  <div
                    key={cat.slug}
                    id={`cat-${cat.slug}`}
                    role="tabpanel"
                    aria-labelledby={`alacarte-tab-${cat.slug}`}
                    className="mt-5 scroll-mt-24"
                    data-testid={`alacarte-panel-${cat.slug}`}
                  >
                    {cat.description && (
                      <div className="text-xs text-zinc-500 mb-3">{cat.description}</div>
                    )}
                    <div className="fl-card overflow-hidden">
                      <div className="grid grid-cols-12 p-4 border-b fl-label" style={{ borderColor: "var(--fl-border)" }}>
                        <div className="col-span-5 md:col-span-5">Service</div>
                        <div className="col-span-3 hidden md:block">Details</div>
                        <div className="col-span-3 md:col-span-2 text-right">Price</div>
                        <div className="col-span-4 md:col-span-2 text-right">Book</div>
                      </div>
                      {cat.services.map((s) => (
                        <div
                          key={s.id}
                          className="grid grid-cols-12 items-center gap-2 p-4 border-b last:border-0 text-sm hover:bg-white/[0.02]"
                          style={{ borderColor: "var(--fl-border)" }}
                          data-testid={`alacarte-row-${s.id}`}
                        >
                          <div className="col-span-5 md:col-span-5">
                            <div className="fl-heading text-base">
                              {s.name}
                              {s.duration_label ? <span className="text-zinc-500 text-xs ml-2">· {s.duration_label}</span> : s.duration_min ? <span className="text-zinc-500 text-xs ml-2">· {s.duration_min} min</span> : null}
                            </div>
                            {s.description && <div className="text-xs text-zinc-500 mt-0.5 md:hidden">{s.description}</div>}
                            {(s.included_in || []).length > 0 && (() => {
                              const cheapest = upsell(s);
                              return (
                                <div className="flex flex-wrap items-center gap-2 mt-1.5" data-testid={`alacarte-perks-${s.id}`}>
                                  {s.included_in.map((mid) => {
                                    if (!memNameById[mid]) return null;
                                    const m = memNameById[mid] || mid;
                                    const mem = memberships.find((mm) => mm.id === mid);
                                    const priceBit = mem ? ` · £${Math.round(mem.price)}/month` : "";
                                    return (
                                      <span
                                        key={mid}
                                        className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 uppercase tracking-wider cursor-help"
                                        title={`Included in ${m}${priceBit} — active members get this session at no extra cost.`}
                                        data-testid={`alacarte-perk-${s.id}-${mid}`}
                                      >
                                        Incl. in {m}
                                      </span>
                                    );
                                  })}
                                  {cheapest && (
                                    <a href="#memberships"
                                       className="text-[10px] text-[#f47a20] hover:text-orange-300 transition-colors flex items-center gap-1"
                                       data-testid={`alacarte-upsell-${s.id}`}
                                       title={`${cheapest.name} covers this service at £${Math.round(cheapest.price)}/${cheapest.billing || 'mo'}`}>
                                      Save with {memNameById[cheapest.id] || cheapest.name} · from £{Math.round(cheapest.price)}/mo
                                      <ArrowRight size={9} />
                                    </a>
                                  )}
                                </div>
                              );
                            })()}
                            {/* Preparation hint — appears on assessment / consultation sessions so
                                new clients know what to bring before they book. */}
                            {/assessment|consultation|screening/i.test(s.name || "") && (
                              <div className="text-[11px] italic mt-2 flex items-start gap-1.5" style={{ color: "var(--fl-muted)" }} data-testid={`alacarte-prep-${s.id}`}>
                                <Info size={11} className="text-[#f47a20] mt-0.5 shrink-0" />
                                <span>Please bring comfortable activewear — you'll be guided through a movement screen.</span>
                              </div>
                            )}
                          </div>
                          <div className="col-span-3 hidden md:block text-xs text-zinc-500">{s.description || "—"}</div>
                          <div className={`col-span-3 md:col-span-2 text-right fl-heading text-[#f47a20] ${(s.show_public_price === false || s._approvedQuoted) ? "text-[11px] md:text-xs font-semibold" : "text-base md:text-lg"}`} data-testid={`alacarte-price-${s.id}`}>
                            {s._approvedQuoted ? "Individually quoted" : s.show_public_price === false ? (s.public_price_message || "Contact us") : `£${Math.round(s.price)}`}
                          </div>
                          <div className="col-span-4 md:col-span-2 text-right">
                            {s._checkoutDisabled ? (
                              <span
                                className="text-[10px] md:text-[11px] px-2 py-1.5 inline-flex items-center justify-center gap-1 w-full rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 whitespace-nowrap leading-tight"
                                title="Preview only — checkout is being updated to this price in the next release."
                                data-testid={`alacarte-book-disabled-${s.id}`}
                              >
                                Booking update in progress
                              </span>
                            ) : (
                              <Link
                                to={(s.show_public_price === false || s._approvedQuoted) ? `/contact?topic=service&service=${encodeURIComponent(s.id)}` : bookHref(s)}
                                className="fl-btn text-[11px] px-2 md:px-3 py-1.5 inline-flex items-center justify-center gap-1 w-full whitespace-nowrap"
                                data-testid={`alacarte-book-${s.id}`}
                              >
                                {(s.show_public_price === false || s._approvedQuoted) ? "Enquire" : "Book"} <ArrowRight size={11} className="hidden sm:inline" />
                              </Link>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="text-[11px] text-zinc-500 mt-3 flex items-start gap-1">
            <Info size={11} className="mt-0.5 shrink-0" />
            Prices include VAT where applicable. Blocks of 5 sessions receive a 10% discount at checkout.
          </div>

          {/* Membership upsell — elevated, standalone card so users actively
              browse plans instead of stumbling across the small tag under
              a single session row. */}
          {memberships.length > 0 && (
            <div className="fl-card fl-card-elevated mt-6 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 border-l-4 border-l-[#f47a20]" data-testid="alacarte-membership-upsell">
              <div className="flex-1 min-w-0">
                <div className="fl-label text-[#f47a20]">Booking more than once?</div>
                <div className="fl-heading text-xl md:text-2xl mt-1">Save with a monthly plan.</div>
                <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
                  Memberships include recurring clinician sessions, remote reviews and messaging support — an ongoing way to stay supported between appointments.
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs" data-testid="alacarte-upsell-chips">
                  {memberships.slice(0, 4).map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setActiveMain("memberships")}
                      className="fl-btn-anim inline-flex items-center gap-2 px-3 py-1.5 rounded-full border hover:border-[#f47a20]/60 transition-colors"
                      style={{ borderColor: "var(--fl-border)", color: "var(--fl-text)" }}
                      data-testid={`alacarte-upsell-chip-${m.id}`}
                    >
                      <span className="fl-heading text-sm">{m.name.replace(" Membership", "")}</span>
                      <span className="text-[11px] text-[#f47a20] font-semibold">£{Math.round(m.price)}/mo</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="shrink-0 flex flex-col gap-2 md:min-w-[180px]">
                <a href="#memberships" className="fl-btn text-xs justify-center" data-testid="alacarte-goto-memberships">
                  Compare all plans <ArrowRight size={12} />
                </a>
                <Link to="/service-finder" className="fl-btn-ghost text-xs justify-center" data-testid="alacarte-goto-finder">
                  Not sure? Take the finder
                </Link>
              </div>
            </div>
          )}

          {/* Cancellation & policy strip — reassures visitors right at the
              decision point, cuts checkout hesitation. */}
          <div className="mt-4 fl-card p-4 md:p-5 text-xs leading-relaxed" data-testid="alacarte-policy">
            <div className="fl-label text-[#f47a20] mb-1.5">Fair booking policy</div>
            <ul className="grid md:grid-cols-2 gap-x-6 gap-y-1" style={{ color: "var(--fl-muted)" }}>
              <li>· Free cancellation or reschedule up to <b>24 hours</b> before your session.</li>
              <li>· Cancellations inside 24 hours are charged in full — please give us as much notice as you can.</li>
              <li>· Missed appointments (no-shows) are charged in full.</li>
              <li>· All bookings are confirmed by email with reschedule and cancel links.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ================ Memberships (shown before programmes) ================ */}
      <section id="memberships" className={`py-24 md:py-32 scroll-mt-24 bg-[#1A1A1A] border-t border-white/5 ${activeMain !== "memberships" ? "hidden" : ""}`} data-testid="prices-section-memberships">
        <div className="max-w-7xl mx-auto px-6">
          <div className="fl-label text-[#f47a20]">Monthly Memberships</div>
          <h2 className="fl-heading text-3xl md:text-4xl mt-2">Ongoing support, month by month.</h2>
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            {memberships.map((m) => {
              const description = m.short_description || m.tagline || "";
              const keyPoints = (m.card_highlights && m.card_highlights.length > 0) ? m.card_highlights : (m.features || []);
              return (
                <div key={m.id} className={`fl-card p-6 md:p-7 flex flex-col ${m.popular ? "border-[#f47a20]" : ""}`} data-testid={`mem-card-${m.id}`}>
                  {/* Header row: label + price */}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="fl-label text-[#f47a20]">{m.badge || (m.popular ? "Most popular" : "Membership")}</div>
                      <div className="fl-heading text-2xl mt-2">{m.name}</div>
                      {m.headline && (
                        <div className="text-sm italic mt-1" style={{ color: "var(--fl-muted)" }} data-testid={`mem-headline-${m.id}`}>
                          {m.headline}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="flex items-baseline gap-1 justify-end">
                        <span className="fl-heading text-4xl text-[#f47a20]">£{m.price}</span>
                        <span className="text-sm text-zinc-500">/ month</span>
                      </div>
                      {m.billing?.weekly && (
                        <div className="text-xs text-zinc-500 mt-1">or £{m.billing.weekly.amount.toFixed(2)} weekly</div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {description && (
                    <p className="text-sm leading-relaxed mt-4" style={{ color: "var(--fl-text-2)" }} data-testid={`mem-description-${m.id}`}>
                      {description}
                    </p>
                  )}

                  {/* Best for */}
                  {m.best_for && (
                    <div className="text-xs mt-3 leading-relaxed" style={{ color: "var(--fl-muted)" }} data-testid={`mem-best-for-${m.id}`}>
                      <b>Best for:</b> {m.best_for}
                    </div>
                  )}

                  {/* Key points */}
                  {keyPoints.length > 0 && (
                    <>
                      <div className="fl-label text-[#f47a20] mt-5 mb-2">Key points</div>
                      <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-zinc-300 flex-1" data-testid={`mem-keypoints-${m.id}`}>
                        {keyPoints.map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check size={13} className="text-[#f47a20] mt-1 shrink-0" /> <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {!m._approved && (memPerks[m.id] || []).length > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/5" data-testid={`mem-perks-${m.id}`}>
                      <div className="fl-label mb-1.5 text-emerald-400">À-la-carte included</div>
                      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-400">
                        {memPerks[m.id].map((p) => (
                          <li key={p.name} className="flex items-center gap-1">
                            <span className="text-emerald-400">✓</span>
                            <Link to={`/prices#cat-${p.cat}`} className="hover:text-white transition-colors">{p.name}</Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-6">
                    <Link
                      to={m.cta_destination || `/checkout/summary?package=${m.id}&plan=membership`}
                      className="fl-btn-anim fl-btn text-sm justify-center w-full"
                      data-testid={`mem-choose-${m.id}`}
                    >
                      {m.cta_text || "Join membership"} <ArrowRight size={13} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ======= £95 assessment-credit promotion — PRESENTATION ONLY ======= */}
          <div className="mt-10 fl-card fl-card-elevated p-6 md:p-8 relative overflow-hidden border-l-4 border-l-[#f47a20]" data-testid="promo-assessment-credit">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="fl-label text-[#f47a20]">New patient offer</span>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300" data-testid="promo-status-badge">
                {ASSESSMENT_CREDIT_PROMO.status}
              </span>
            </div>
            <h3 className="fl-heading text-2xl md:text-3xl">{ASSESSMENT_CREDIT_PROMO.title}</h3>
            <p className="text-sm mt-3 max-w-3xl leading-relaxed" style={{ color: "var(--fl-text-2)" }}>
              {ASSESSMENT_CREDIT_PROMO.body}
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              {ASSESSMENT_CREDIT_PROMO.examples.map((ex) => (
                <div key={ex.plan} className="fl-card p-4" data-testid={`promo-example-${ex.plan.toLowerCase()}`}>
                  <div className="fl-heading text-lg">{ex.plan} membership</div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="fl-heading text-3xl text-[#f47a20]">£{ex.firstMonth}</span>
                    <span className="text-xs" style={{ color: "var(--fl-muted)" }}>first month</span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>
                    £95 assessment fee credited to month one, then £{ex.recurring}/month.
                  </p>
                  <Link
                    to={`/checkout/summary?package=${ex.plan.toLowerCase()}&plan=membership`}
                    className="fl-btn-anim fl-btn text-xs justify-center w-full mt-3"
                    data-testid={`promo-join-${ex.plan.toLowerCase()}`}
                  >
                    Start {ex.plan} <ArrowRight size={12} />
                  </Link>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <div className="fl-label text-[#f47a20] mb-2">Eligibility</div>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs" style={{ color: "var(--fl-muted)" }}>
                {ASSESSMENT_CREDIT_PROMO.eligibility.map((e) => (
                  <li key={e} className="flex items-start gap-2"><Check size={12} className="text-[#f47a20] mt-0.5 shrink-0" /> {e}</li>
                ))}
              </ul>
            </div>
            <div className="mt-6 text-[11px] p-3 rounded-md flex items-start gap-2" style={{ background: "color-mix(in oklab, #16A34A 6%, transparent)", border: "1px solid #16A34A44", color: "#4ade80" }} data-testid="promo-pending-note">
              <Info size={13} className="mt-0.5 shrink-0" />
              <span>Complete your £95 initial assessment first. When you then choose a Progress or Performance membership within seven days, the £95 credit is applied automatically to your first month at checkout for eligible new patients.</span>
            </div>
          </div>
        </div>
      </section>

      {/* ================ Programmes (API-driven; public price hidden) ================ */}
      {programmes.length > 0 && (
        <section id="packages" className={`py-24 md:py-32 bg-[#0A0A0A] border-t border-white/5 scroll-mt-24 ${activeMain !== "packages" ? "hidden" : ""}`} data-testid="prices-section-packages">
          <div className="max-w-7xl mx-auto px-6">
            <div className="fl-label text-[#f47a20]">Rehabilitation Programmes</div>
            <h2 className="fl-heading text-3xl md:text-4xl mt-2">Structured, outcome-driven programmes.</h2>
            <p className="text-sm mt-3 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
              Every programme is quoted individually so we can tailor the number of sessions and inclusions to your needs. Tap <b>View full details</b> to see what's involved.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="prices-programmes">
              {programmes.map((p) => (
                <ProgrammeApiCard key={p.id} pkg={p} onOpen={() => setSelectedPkg(p)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================ Online programmes (self-guided PDFs) ================ */}
      <section id="online-programmes" className={`py-24 md:py-32 bg-[#121212] border-t border-white/5 scroll-mt-32 ${activeMain !== "online" ? "hidden" : ""}`} data-testid="prices-section-online">
        <div className="max-w-7xl mx-auto px-6">
          <div className="fl-label text-[#f47a20]">Online programmes</div>
          <h2 className="fl-heading text-3xl md:text-4xl mt-2">6-week self-guided rehab.</h2>
          <p className="text-sm mt-3 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
            Structured, progressive plans you can follow at your own pace — video demos, printable PDF and
            weekly check-ins. Because these are self-guided, we ask a short safety questionnaire before you buy.
          </p>
          <div className="mt-4 inline-flex items-start gap-2 text-[11px] p-3 rounded-md" style={{ background: "color-mix(in oklab, #10B981 6%, transparent)", border: "1px solid #10B98144", color: "#6EE7B7" }}>
            <ShieldCheck size={13} className="mt-0.5 shrink-0" />
            <span>Every purchase is preceded by a red / amber / green screening. If your answers suggest a self-guided programme isn't safe for you, we'll offer a clinician assessment instead — no charge to check.</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="prices-online-list">
            {onlineProgrammes.length === 0 && (
              <div className="fl-card p-8 text-center text-sm text-zinc-500 col-span-full" data-testid="prices-online-empty">
                Loading programmes…
              </div>
            )}
            {onlineProgrammes.map((p) => <OnlineProgrammeCard key={p.id} pkg={p} onOpen={() => setSelectedPkg(p)} />)}
            <div className="fl-card p-6 flex flex-col border-dashed border-[#f47a20]/40" data-testid="prices-online-ankle">
              <span className="self-start inline-block text-[10px] font-bold tracking-[0.2em] px-2 py-0.5 rounded-full bg-white/10 text-[#f47a20] mb-3">IN DEVELOPMENT</span>
              <div className="fl-heading text-xl">Ankle programmes</div>
              <p className="text-sm text-zinc-400 mt-2 flex-1">Achilles, impingement, instability and more. Use the five-minute Ankle Programme Finder to get your pathway, personalised support and early access at launch.</p>
              <Link to="/online-programmes/ankle" className="fl-btn text-sm justify-center mt-5" data-testid="prices-online-ankle-cta">Find my ankle pathway</Link>
            </div>
          </div>
        </div>
      </section>

      {selectedPkg && (
        <PackageDetailsModal pkg={selectedPkg} onClose={() => setSelectedPkg(null)} isLoggedIn={!!user} />
      )}

      {/* ================ Payment conditions ================ */}
      {cms.isVisible("paymentConditions") && (
      <section className="py-24 md:py-32 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="fl-card p-6 md:p-8" data-testid="payment-conditions">
            <div className="fl-label text-[#f47a20] mb-2">{cms.text("paymentConditions", "eyebrow", "Payment conditions")}</div>
            <ul className="text-xs md:text-sm space-y-1.5 text-zinc-300">
              <li>· Pay in full is the best-value option and unlocks the saving shown on each package.</li>
              <li>· Weekly instalments offer greater payment flexibility over the programme.</li>
              <li>· Package instalments are payment plans for the complete package — not individual pay-as-you-go sessions.</li>
              <li>· Clients remain committed to completing payment for the package after starting it.</li>
              <li>· Monthly memberships operate as rolling memberships with cancellation subject to Functional LAB's terms and notice period.</li>
              <li>· All sessions should be used within the stated package duration unless an extension is agreed in advance.</li>
            </ul>
          </div>
        </div>
      </section>
      )}

      {/* ================ Discovery-call / support CTA ================ */}
      {cms.isVisible("stillDeciding") && (
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#121212] via-[#170f0d] to-[#0A0A0A] border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 fl-card p-6 md:p-8 text-center">
          <div className="fl-label text-[#f47a20] mb-2">{cms.text("stillDeciding", "eyebrow", "Still deciding?")}</div>
          <div className="fl-heading text-2xl md:text-3xl">{cms.text("stillDeciding", "heading", "Not sure which plan is right?")}</div>
          <p className="text-sm text-zinc-400 mt-3">
            We'll discuss your injury, recovery goals and the level of support you may need — then recommend the plan that fits.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" className="fl-btn-anim fl-btn text-sm justify-center" data-testid="prices-talk-to-us">{cms.text("stillDeciding", "ctaLabel", "Talk to us")}</Link>
            <Link to="/services" className="fl-btn-anim fl-btn-ghost text-sm justify-center">See all services</Link>
          </div>
        </div>
      </section>
      )}

      <PublicFooter />
    </div>
  );
}

function OnlineProgrammeCard({ pkg, onOpen }) {
  const { user } = useAuth();
  const highlights = (pkg.card_highlights && pkg.card_highlights.length > 0)
    ? pkg.card_highlights
    : (pkg.features || []).slice(0, 5);
  const suitabilityPath = pkg.cta_destination || `/programmes/check-suitability/${pkg.id}`;
  // Logged-out visitors go via /register?redirect=… so they land back on the
  // screening with their session ready — otherwise they'd fill 16 questions
  // and only *then* be bounced to register, losing every answer.
  const ctaDest = user ? suitabilityPath : `/register?redirect=${encodeURIComponent(suitabilityPath)}`;
  return (
    <div className="fl-card p-6 flex flex-col hover:border-[#f47a20]/50 hover:-translate-y-0.5 transition-all" data-testid={`prices-online-${pkg.id}`}>
      {pkg.badge && (
        <span className="self-start inline-block text-[10px] font-bold tracking-[0.2em] px-2 py-0.5 rounded-full bg-[#f47a20] text-white mb-3">
          {pkg.badge}
        </span>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="fl-heading text-2xl">{pkg.name}</div>
          {pkg.duration && (
            <div className="text-xs mt-1" style={{ color: "var(--fl-muted-2)" }}>{pkg.duration}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="fl-heading text-3xl text-[#f47a20]">£{Math.round(pkg.price)}</div>
          <div className="text-[10px] text-zinc-500">one-off</div>
        </div>
      </div>
      {pkg.headline && (
        <div className="text-sm italic mt-3" style={{ color: "var(--fl-muted)" }}>{pkg.headline}</div>
      )}
      <ul className="mt-4 space-y-1.5 text-sm flex-1" style={{ color: "var(--fl-text-2)" }}>
        {highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check size={13} className="text-[#f47a20] mt-1 shrink-0" /> <span>{h}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 flex flex-col gap-2">
        <button
          type="button"
          onClick={onOpen}
          className="fl-btn-anim fl-btn-ghost text-xs justify-center"
          data-testid={`prices-online-details-${pkg.id}`}
        >
          View full details
        </button>
        <Link
          to={ctaDest}
          className="fl-btn-anim fl-btn text-sm justify-center"
          data-testid={`prices-online-cta-${pkg.id}`}
        >
          {pkg.cta_text || "Check suitability"} <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}

function ProgrammeApiCard({ pkg, onOpen }) {
  const highlights = (pkg.card_highlights && pkg.card_highlights.length > 0)
    ? pkg.card_highlights
    : (pkg.features || []).slice(0, 5);
  return (
    <div className={`fl-card p-6 flex flex-col ${pkg.popular ? "border-[#f47a20]" : ""}`} data-testid={`prices-programme-${pkg.id}`}>
      {pkg.badge && (
        <span className="self-start inline-block text-[10px] font-bold tracking-[0.2em] px-2 py-0.5 rounded-full bg-[#f47a20] text-white mb-3">
          {pkg.badge}
        </span>
      )}
      <div className="fl-heading text-xl">{pkg.name}</div>
      {pkg.headline && (
        <div className="text-sm italic mt-1" style={{ color: "var(--fl-muted)" }}>{pkg.headline}</div>
      )}
      <div className="mt-4 text-sm font-semibold text-[#f47a20]" data-testid={`prices-programme-contact-${pkg.id}`}>
        Contact us for more information
      </div>
      {pkg.duration && (
        <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted-2)" }}>Duration · {pkg.duration}</div>
      )}
      {pkg.best_for && (
        <div className="text-[11px] mt-3 leading-relaxed" style={{ color: "var(--fl-muted)" }}>
          <b>Best for:</b> {pkg.best_for}
        </div>
      )}
      <ul className="mt-4 space-y-1.5 text-sm flex-1" style={{ color: "var(--fl-text-2)" }}>
        {highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check size={13} className="text-[#f47a20] mt-1 shrink-0" /> <span>{h}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 flex flex-col gap-2">
        <button
          type="button"
          onClick={onOpen}
          className="fl-btn-anim fl-btn-ghost text-xs justify-center"
          data-testid={`prices-programme-details-${pkg.id}`}
        >
          View full details
        </button>
        <Link
          to={pkg.cta_destination || "/contact"}
          className="fl-btn-anim fl-btn text-xs justify-center"
          data-testid={`prices-programme-cta-${pkg.id}`}
        >
          {pkg.cta_text || "Contact us for more information"}
        </Link>
      </div>
    </div>
  );
}

function PackageCard({ pkg: p, isLoggedIn }) {
  const [plan, setPlan] = useState("full");
  const weeklyTotal = (p.weekly_n * p.weekly_amt).toFixed(2);
  const buyHref = useMemo(() => {
    return `/checkout/summary?package=${p.id}&plan=${plan}`;
  }, [p.id, plan]);

  return (
    <div
      className={`fl-card p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${p.popular ? "border-[#f47a20]" : ""}`}
      data-testid={`prices-pkg-${p.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="fl-label text-[#f47a20]">{p.popular ? "Most popular" : "Package"}</div>
          <div className="fl-heading text-2xl mt-2">{p.name}</div>
          <div className="text-xs text-zinc-400 mt-1">Recommended duration: <b>{p.duration}</b></div>
        </div>
        <div className="text-right shrink-0">
          <div className="fl-heading text-4xl text-[#f47a20]">£{p.full}</div>
          <div className="text-[10px] text-zinc-500">total · pay in full</div>
        </div>
      </div>
      <div className="text-sm text-zinc-400 italic mt-3">{p.sub}</div>

      <div className="mt-4 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Payment plan">
        <button
          type="button"
          onClick={() => setPlan("weekly")}
          role="radio"
          aria-checked={plan === "weekly"}
          className={`text-left p-3 rounded-md border transition-all duration-200 ${
            plan === "weekly"
              ? "border-[#f47a20] bg-[color:color-mix(in_oklab,#f47a20_10%,transparent)] scale-[1.02]"
              : "border-white/10 hover:border-white/25 hover:-translate-y-0.5"
          }`}
          data-testid={`prices-plan-weekly-${p.id}`}
        >
          <div className={`fl-label ${plan === "weekly" ? "text-[#f47a20]" : ""}`}>Weekly instalments</div>
          <div className="text-sm text-zinc-100 mt-1">{p.weekly_n} × £{p.weekly_amt.toFixed(2)}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">Total £{weeklyTotal}</div>
        </button>
        <button
          type="button"
          onClick={() => setPlan("full")}
          role="radio"
          aria-checked={plan === "full"}
          className={`text-left p-3 rounded-md border transition-all duration-200 ${
            plan === "full"
              ? "border-[#f47a20] bg-[color:color-mix(in_oklab,#f47a20_10%,transparent)] scale-[1.02]"
              : "border-white/10 hover:border-white/25 hover:-translate-y-0.5"
          }`}
          data-testid={`prices-plan-full-${p.id}`}
        >
          <div className={`fl-label ${plan === "full" ? "text-[#f47a20]" : ""}`}>Pay in full</div>
          <div className="text-sm text-zinc-100 mt-1">£{p.full}</div>
          <div className="text-[10px] text-emerald-400 mt-0.5 font-semibold">Save £{p.save}</div>
        </button>
      </div>

      <div className="fl-label mt-5 mb-2">What's included</div>
      <ul className="space-y-2 text-sm text-zinc-300 flex-1">
        {p.feat.map((f) => (
          <li key={f} className="flex items-start gap-2"><span className="text-[#f47a20] mt-0.5">✓</span> {f}</li>
        ))}
      </ul>

      {p.notice && (
        <div className="mt-4 text-[11px] p-2.5 rounded-md" style={{ background: "color-mix(in oklab, #F59E0B 6%, transparent)", border: "1px solid #F59E0B44", color: "#F59E0B" }}>
          {p.notice}
        </div>
      )}

      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <Link to={buyHref} className="fl-btn-anim fl-btn text-xs flex-1 justify-center group" data-testid={`prices-pkg-choose-${p.id}`}>
          {isLoggedIn ? "Choose & pay" : "Create account & pay"} <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
        <Link to="/contact" className="fl-btn-anim fl-btn-ghost text-xs flex-1 justify-center" data-testid={`prices-pkg-speak-${p.id}`}>
          Speak to a clinician
        </Link>
      </div>
    </div>
  );
}
